"use client";

import { useState, useMemo } from "react";
import { CampaignCard } from "@/components/campaigns/CampaignCard";
import { getDisplayStatus, FILTERABLE_STATUSES } from "@/lib/campaign-status";
import type { CategoryId } from "@/lib/categories";

interface Campaign {
  id: string;
  slug: string;
  title: string;
  headline?: string;
  description: string | null;
  orgName: string | null;
  locationText?: string;
  coverImageUrl?: string;
  categories: string[];
  status: string;
  startAt: string | null;
  endAt: string | null;
  createdAt: string;
  participantCount: number;
  clueCount: number;
}

interface CampaignListSectionProps {
  tenant: {
    name: string;
    slug: string;
    logoUrl?: string;
    primaryColor: string;
    accentColor?: string;
  };
  campaigns: Campaign[];
  title?: string | null;
  description?: string | null;
  whitelabel?: boolean;
}

export function CampaignListSection({
  tenant,
  campaigns,
  title,
  description,
  whitelabel,
}: CampaignListSectionProps) {
  const [showArchived, setShowArchived] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("all");

  const filteredCampaigns = useMemo(() => {
    let filtered = campaigns;
    if (!showArchived) {
      filtered = filtered.filter((campaign) => campaign.status !== "ARCHIVED");
    }
    if (selectedStatus !== "all") {
      filtered = filtered.filter((campaign) => getDisplayStatus(campaign) === selectedStatus);
    }
    return filtered;
  }, [campaigns, showArchived, selectedStatus]);

  const displayTitle = title || "All Experiences";

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2
            className="text-3xl font-bold text-gray-900"
            style={whitelabel ? { color: 'var(--theme-text, #111827)' } : undefined}
          >
            {displayTitle}
          </h2>
          {description && (
            <p
              className="mt-2 text-gray-500"
              style={whitelabel ? { color: 'var(--theme-text, #6B7280)', opacity: 0.8 } : undefined}
            >
              {description}
            </p>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label
              className="text-sm font-medium text-gray-700"
              style={whitelabel ? { color: 'var(--theme-text, #374151)' } : undefined}
            >
              Status:
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm bg-white text-gray-900"
              style={whitelabel ? {
                backgroundColor: 'var(--theme-bg, #FFFFFF)',
                color: 'var(--theme-text, #111827)',
              } : undefined}
            >
              <option value="all">All</option>
              {FILTERABLE_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center">
            <input
              id="showArchived"
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 focus:ring-2 border-gray-300 rounded accent-blue-600"
              style={whitelabel ? { accentColor: 'var(--theme-primary, #3B82F6)' } : undefined}
            />
            <label
              htmlFor="showArchived"
              className="ml-2 text-sm text-gray-700"
              style={whitelabel ? { color: 'var(--theme-text, #374151)' } : undefined}
            >
              Show archived
            </label>
          </div>
        </div>
      </div>

      {/* Campaigns Grid */}
      {filteredCampaigns.length === 0 ? (
        <div className="text-center py-12">
          <p
            className="text-gray-500"
            style={whitelabel ? { color: 'var(--theme-text, #6B7280)', opacity: 0.8 } : undefined}
          >
            No campaigns match your current filters.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCampaigns.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={{
                id: campaign.id,
                title: campaign.title,
                headline: campaign.headline,
                description: campaign.description || "",
                slug: campaign.slug,
                orgName: campaign.orgName || tenant.name,
                locationText: campaign.locationText || "",
                categories: (campaign.categories || []) as CategoryId[],
                coverImageUrl: campaign.coverImageUrl,
                clueCount: campaign.clueCount,
                claimCount: campaign.participantCount,
                status: campaign.status as 'DRAFT' | 'PUBLISHED' | 'ACTIVE' | 'ENDED' | 'ARCHIVED',
                startAt: campaign.startAt,
                endAt: campaign.endAt,
                createdAt: new Date(campaign.createdAt),
                tenant: {
                  name: tenant.name,
                  slug: tenant.slug,
                  logoUrl: tenant.logoUrl,
                },
              }}
              primaryColor={whitelabel ? tenant.primaryColor : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}
